import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';

const sideToPosition = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
};

// Compute handle style for a connector at a given side + position (0/1/2 = 20%/50%/80%)
// Offset pushes handles outside the frame border so they sit clearly on the edge
const HANDLE_OFFSET = -7;
const getHandleStyle = (side, position) => {
  const pct = [20, 50, 80][position] || 50;
  switch (side) {
    case 'top':    return { left: `${pct}%`, top: HANDLE_OFFSET, transform: 'translateX(-50%)' };
    case 'bottom': return { left: `${pct}%`, bottom: HANDLE_OFFSET, transform: 'translateX(-50%)' };
    case 'left':   return { top: `${pct}%`, left: HANDLE_OFFSET, transform: 'translateY(-50%)' };
    case 'right':  return { top: `${pct}%`, right: HANDLE_OFFSET, transform: 'translateY(-50%)' };
    default:       return {};
  }
};

const NODE_W = 140;
const NODE_H = 140;

const AssetNode = memo(({ data, selected }) => {
  const color = data.color || '#4ecca3';
  const lighterColor = color + '55';

  // Use connectors from the definition if available, otherwise default 4 center handles
  const connectors = (data.connectors && Array.isArray(data.connectors))
    ? data.connectors.filter((c) => c.enabled)
    : [
        { id: 'top-1', side: 'top', position: 1, enabled: true, favorite: true },
        { id: 'right-1', side: 'right', position: 1, enabled: true, favorite: true },
        { id: 'bottom-1', side: 'bottom', position: 1, enabled: true, favorite: true },
        { id: 'left-1', side: 'left', position: 1, enabled: true, favorite: true },
      ];

  // Capacity from properties
  const capacity = data.properties?.capacity;
  const capacityStr = capacity ? `${capacity} MWac` : '';

  return (
    <div
      style={{
        width: NODE_W,
        height: NODE_H,
        position: 'relative',
        background: '#16213e',
        border: `2px solid ${color}`,
        borderRadius: 6,
        boxShadow: selected
          ? `0 0 16px ${color}44, 0 0 4px ${color}88`
          : `0 2px 8px rgba(0,0,0,0.4)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
      }}
    >
      {/* Icon */}
      <div style={{ fontSize: 32, lineHeight: 1 }}>{data.icon}</div>

      {/* Label — wraps to 2 lines if needed */}
      <div style={{
        fontSize: 14,
        color: '#e2e8f0',
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 600,
        textAlign: 'center',
        maxWidth: NODE_W - 14,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: 1.2,
        marginTop: 4,
        wordBreak: 'break-word',
      }}>
        {data.label}
      </div>

      {/* Capacity */}
      {capacityStr && (
        <div style={{
          fontSize: 11,
          color: '#94a3b8',
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 500,
          textAlign: 'center',
          lineHeight: 1,
          marginTop: 2,
        }}>
          {capacityStr}
        </div>
      )}

      {/* Connector handles with halo */}
      {connectors.map((c) => {
        const pos = sideToPosition[c.side];
        if (!pos) return null;
        const handleStyle = getHandleStyle(c.side, c.position);
        const isFav = c.favorite;
        const dotSize = isFav ? 10 : 7;
        const dir = c.direction || 'out';
        const handleType = dir === 'in' ? 'target' : dir === 'bidirectional' ? 'source' : 'source';
        return (
          <Handle
            key={c.id}
            id={c.id}
            type={handleType}
            position={pos}
            className="connector-handle"
            style={{
              ...handleStyle,
              width: dotSize,
              height: dotSize,
              background: isFav ? color : lighterColor,
              border: isFav ? `2px solid ${color}` : `1.5px solid ${lighterColor}`,
              borderRadius: '50%',
              cursor: 'crosshair',
              zIndex: 10,
              boxShadow: `0 0 0 3px ${color}33, 0 0 8px ${color}22`,
              transition: 'box-shadow 0.2s, transform 0.2s',
            }}
          />
        );
      })}
    </div>
  );
});

AssetNode.displayName = 'AssetNode';

export default AssetNode;
